import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Search, User, PenSquare, Users } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../lib/supabase';
import { Logo } from '../components/Logo';
import SearchScreen from '../components/SearchScreen';
import NotificationsDialog from '../components/NotificationsDialog';
import CreatePostDialog from '../components/CreatePostDialog';
import PostActions from '../components/PostActions';
import PostAttachment from '../components/PostAttachment';

interface Post {
  id: string;
  content: string;
  created_at: string;
  user: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
  };
  channel?: {
    id: string;
    name: string;
  } | null;
  attachments: Array<{
    id: string;
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnail_url?: string;
  }>;
  likes: number;
  comments: number;
  liked_by_me: boolean;
}

interface Channel {
  id: string;
  name: string;
  description: string | null;
  member_count: number;
  cover_image: string | null;
  role: 'owner' | 'moderator' | 'member';
}

function Feed() {
  const { user } = useUser();
  const [activeTab, setActiveTab] = useState<'friends' | 'channels'>('friends');
  const [showSearch, setShowSearch] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);
  const [notificationCount, setNotificationCount] = useState(0);

  useEffect(() => {
    if (user) {
      if (activeTab === 'friends') {
        fetchFriendsPosts();
      } else {
        fetchUserChannels();
      }
      fetchNotificationCount();
    }
  }, [user, activeTab]);

  const fetchFriendsPosts = async () => {
    if (!user) return;

    try {
      // Get friend IDs from accepted friend requests
      const { data: friendRequests } = await supabase
        .from('friend_requests')
        .select('sender_id, receiver_id')
        .eq('status', 'accepted')
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`);

      if (!friendRequests?.length) {
        setPosts([]);
        setLoading(false);
        return;
      }

      const friendIds = friendRequests.map(request => 
        request.sender_id === user.id ? request.receiver_id : request.sender_id
      );

      // Get posts from friends and own posts
      const { data: postsData } = await supabase
        .from('posts')
        .select(`
          id,
          content,
          created_at,
          user:users(id, username, display_name, avatar_url),
          channel:channels(id, name),
          attachments:post_attachments(
            id,
            type,
            url,
            title,
            description,
            thumbnail_url
          ),
          likes:likes(count),
          comments:comments(count)
        `)
        .in('user_id', [user.id, ...friendIds])
        .order('created_at', { ascending: false });

      const { data: userLikes } = await supabase
        .from('likes')
        .select('post_id')
        .eq('user_id', user.id);

      const likedPostIds = new Set(userLikes?.map(like => like.post_id) || []);

      const formattedPosts = postsData?.map(post => ({
        ...post,
        attachments: post.attachments || [],
        likes: post.likes[0]?.count ?? 0,
        comments: post.comments[0]?.count ?? 0,
        liked_by_me: likedPostIds.has(post.id)
      })) || [];

      setPosts(formattedPosts);
    } catch (error) {
      console.error('Error fetching friends posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserChannels = async () => {
    if (!user) return;

    try {
      const { data: memberships } = await supabase
        .from('channel_members')
        .select(`
          channel:channels(
            id,
            name,
            description,
            member_count,
            cover_image
          ),
          role
        `)
        .eq('user_id', user.id)
        .order('role', { ascending: false });

      setChannels(memberships?.map(m => ({
        ...m.channel,
        role: m.role
      })) || []);
    } catch (error) {
      console.error('Error fetching user channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationCount = async () => {
    if (!user) return;

    try {
      const { count } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);
      
      setNotificationCount(count || 0);
    } catch (error) {
      console.error('Error fetching notification count:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="fixed top-0 left-0 right-0 bg-white border-b z-50">
        <div className="h-14 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <Logo className="h-8 w-8 text-indigo-600" />
            <span className="text-lg font-bold text-indigo-600">SoundLink</span>
          </div>
          
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowSearch(true)}
              className="p-2 text-gray-600 hover:text-indigo-600 transition-colors"
            >
              <Search className="h-6 w-6" />
            </button>

            <button 
              onClick={() => setShowNotifications(true)}
              className="p-2 text-gray-600 hover:text-indigo-600 transition-colors relative"
            >
              <Bell className="h-6 w-6" />
              {notificationCount > 0 && (
                <span className="absolute top-0 right-0 h-4 w-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                  {notificationCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className="px-4 pb-2">
          <div className="bg-gray-100 p-1 rounded-lg grid grid-cols-2 gap-1">
            <button
              onClick={() => setActiveTab('friends')}
              className={`py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'friends'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'hover:bg-white/50 text-gray-600'
              }`}
            >
              Friends
            </button>
            <button
              onClick={() => setActiveTab('channels')}
              className={`py-2 px-4 rounded-lg transition-colors ${
                activeTab === 'channels'
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'hover:bg-white/50 text-gray-600'
              }`}
            >
              Channels
            </button>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-20">
        {activeTab === 'friends' ? (
          <>
            <div className="bg-white px-4 py-3 mb-2">
              <button
                onClick={() => setShowCreatePost(true)}
                className="w-full px-4 py-2 bg-gray-100 rounded-full text-left text-gray-500 hover:bg-gray-200 transition-colors"
              >
                What's on your musical mind?
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white p-8 text-center">
                <PenSquare className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">No Posts Yet</h3>
                <p className="text-gray-600 mb-6">
                  Follow your friends to see their posts here
                </p>
                <Link
                  to="/explore"
                  className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  Explore Users
                </Link>
              </div>
            ) : (
              <div className="space-y-2">
                {posts.map(post => (
                  <div key={post.id} className="bg-white">
                    <div className="px-4 py-3">
                      <div className="flex items-start space-x-3">
                        <Link to={`/profile/${post.user.id}`}>
                          <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden">
                            {post.user.avatar_url ? (
                              <img
                                src={post.user.avatar_url}
                                alt={post.user.username}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <User className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                        </Link>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/profile/${post.user.id}`}
                              className="font-semibold hover:text-indigo-600 transition-colors"
                            >
                              {post.user.display_name || post.user.username}
                            </Link>
                            {post.channel && (
                              <>
                                <span className="text-gray-500">in</span>
                                <Link
                                  to={`/channel/${post.channel.name}`}
                                  className="text-indigo-600 hover:text-indigo-700 transition-colors"
                                >
                                  #{post.channel.name}
                                </Link>
                              </>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">
                            {new Date(post.created_at).toLocaleString()}
                          </p>
                          
                          <p className="text-gray-900 mt-2">{post.content}</p>

                          {post.attachments?.map((attachment) => (
                            <PostAttachment
                              key={attachment.id}
                              type={attachment.type}
                              url={attachment.url}
                              title={attachment.title}
                              description={attachment.description}
                              thumbnailUrl={attachment.thumbnail_url}
                            />
                          ))}

                          <PostActions
                            postId={post.id}
                            userId={post.user.id}
                            likes={post.likes}
                            comments={post.comments}
                            liked={post.liked_by_me}
                            onLikeChange={fetchFriendsPosts}
                            onDelete={fetchFriendsPosts}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="px-4">
            <div className="space-y-4">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                </div>
              ) : channels.length === 0 ? (
                <div className="bg-white p-8 text-center rounded-lg shadow-sm border-2 border-gray-200">
                  <Users className="h-12 w-12 text-indigo-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Channels Yet</h3>
                  <p className="text-gray-600 mb-6">
                    Join or create channels to see them here
                  </p>
                  <Link
                    to="/channel/explore"
                    className="inline-block px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Explore Channels
                  </Link>
                </div>
              ) : (
                channels.map(channel => (
                  <Link
                    key={channel.id}
                    to={`/channel/${channel.name}`}
                    className="block bg-white rounded-lg shadow-sm border-2 border-gray-200 hover:border-indigo-200 transition-colors overflow-hidden"
                  >
                    {channel.cover_image && (
                      <div className="h-32 bg-gray-100">
                        <img
                          src={channel.cover_image}
                          alt={channel.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold">#{channel.name}</h3>
                            {channel.role === 'owner' && (
                              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs rounded-full">
                                Owner
                              </span>
                            )}
                            {channel.role === 'moderator' && (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                Moderator
                              </span>
                            )}
                          </div>
                          {channel.description && (
                            <p className="text-gray-600 mt-1">{channel.description}</p>
                          )}
                        </div>
                        <div className="flex items-center text-gray-500">
                          <Users className="h-4 w-4 mr-1" />
                          {channel.member_count}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showSearch && (
        <SearchScreen onClose={() => setShowSearch(false)} />
      )}

      <CreatePostDialog
        isOpen={showCreatePost}
        onClose={() => setShowCreatePost(false)}
        onSubmit={async (content, attachments) => {
          if (!user) return;

          try {
            const { data: post, error: postError } = await supabase
              .from('posts')
              .insert({
                user_id: user.id,
                content: content.trim()
              })
              .select()
              .single();

            if (postError) throw postError;

            if (attachments.length > 0) {
              const { error: attachmentError } = await supabase
                .from('post_attachments')
                .insert(
                  attachments.map(attachment => ({
                    post_id: post.id,
                    ...attachment
                  }))
                );

              if (attachmentError) throw attachmentError;
            }

            await fetchFriendsPosts();
          } catch (error) {
            console.error('Error creating post:', error);
            throw error;
          }
        }}
      />

      <NotificationsDialog
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        onNotificationsRead={() => setNotificationCount(0)}
      />
    </div>
  );
}

export default Feed;